import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Gavel, 
  ArrowLeft, 
  Save, 
  Download, 
  FileText, 
  Edit3, 
  Scale, 
  ListChecks,
  TrendingUp, 
  FileSpreadsheet, 
  Copy, 
  Plus, 
  Trash2,
  AlertTriangle,
  FolderOpen,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '../config';

export default function VertenzaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pratica, setPratica] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('anagrafica');
  const [offertaTransazione, setOffertaTransazione] = useState(0);
  const [ccnlList, setCcnlList] = useState([]);
  
  // Scenari State
  const [opzioni, setOpzioni] = useState({
    escludiTfr: false,
    escludiAccessori: false,
    escludiAggiuntivi: false,
    percentualeRivalutazione: 100.0,
    percentualeInteressi: 100.0
  });

  // Selected month state for editing
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [replicaFinoAMese, setReplicaFinoAMese] = useState('');
  const [monthEditData, setMonthEditData] = useState({
    vociRichieste: [],
    vociApplicate: []
  });

  // Atti text state
  const [selectedAttoTipo, setSelectedAttoTipo] = useState('diffida');
  const [attoText, setAttoText] = useState('');
  const [loadingAtto, setLoadingAtto] = useState(false);

  // New importo aggiuntivo form
  const [newAggiuntivo, setNewAggiuntivo] = useState({
    tipo: 'ferie',
    descrizione: '',
    importo: 0,
    applicaAccessori: true
  });

  // Generator State for scatti in bulk
  const [genScatti, setGenScatti] = useState({
    mesePrimoScatto: '',
    importoScatto: 20.66,
    frequenzaMesi: 36,
    scattiPartenza: 0,
    maxScatti: 10,
    codiceVoce: 'SCA',
    descrizioneVoce: 'Scatto Anzianità Garantito'
  });

  useEffect(() => {
    fetchPratica();
    fetchCcnlList();
  }, [id]);

  useEffect(() => {
    if (pratica && pratica.periodi && pratica.periodi.length > 0 && !genScatti.mesePrimoScatto) {
      // Imposta come default il primo mese della pratica
      const primoMese = pratica.periodi[0].annoMese;
      setGenScatti(prev => ({ ...prev, mesePrimoScatto: primoMese }));
    }
  }, [pratica]);

  useEffect(() => {
    if (pratica?.ccnlCodice && pratica?.dataAssunzione) {
      const refDate = pratica.dataAssunzione.length === 7 ? `${pratica.dataAssunzione}-01` : pratica.dataAssunzione;
      axios.get(`${API_URL}/sindacato/ccnl/${pratica.ccnlCodice}`, {
        params: { data: refDate }
      }).then(response => {
        const ccnl = response.data;
        if (ccnl) {
          const matchingLivello = ccnl.livelli?.find(l => l.livello === pratica.livello);
          const scattoImporto = matchingLivello?.scatto_anzianita_importo ?? ccnl.scatti_anzianita?.importo ?? 20.66;
          const freqMesi = (ccnl.scatti_anzianita?.ogni_anni ?? 3) * 12;
          const maxScatti = ccnl.scatti_anzianita?.massimo_scatti ?? 10;
          
          setGenScatti(prev => ({
            ...prev,
            mesePrimoScatto: pratica.periodoDa || (pratica.dataAssunzione?.slice(0, 7)),
            importoScatto: scattoImporto,
            frequenzaMesi: freqMesi,
            maxScatti: maxScatti
          }));
        }
      }).catch(err => {
        console.error('Errore nel caricamento del dettaglio CCNL per gli scatti:', err);
      });
    }
  }, [pratica?.ccnlCodice, pratica?.livello, pratica?.dataAssunzione]);

  useEffect(() => {
    if (pratica) {
      setOffertaTransazione(pratica.propostaTransazione || 0);
    }
  }, [pratica]);

  useEffect(() => {
    if (activeTab === 'atti' && pratica) {
      fetchAtto();
    }
  }, [activeTab, selectedAttoTipo]);

  const fetchPratica = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sindacato/vertenza/${id}`);
      setPratica(response.data);
      if (response.data.opzioni) {
        setOpzioni(response.data.opzioni);
      }
    } catch (err) {
      console.error(err);
      setError('Pratica non trovata o errore di caricamento.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCcnlList = async () => {
    try {
      const response = await axios.get(`${API_URL}/sindacato/ccnl`);
      setCcnlList(response.data.ccnl || []);
    } catch (err) {
      console.error('Errore nel caricamento dei CCNL:', err);
    }
  };

  const getAggregatedVociDiff = () => {
    if (!pratica?.risultato?.periodi) return [];
    const aggregates = {};
    pratica.risultato.periodi.forEach(p => {
      if (!p.voci) return;
      p.voci.forEach(v => {
        if (!aggregates[v.codice]) {
          aggregates[v.codice] = {
            codice: v.codice,
            descrizione: v.descrizione || v.codice,
            totaleRichiesto: 0.0,
            totaleApplicato: 0.0,
            totaleDifferenza: 0.0
          };
        }
        aggregates[v.codice].totaleRichiesto += v.richiesto || 0.0;
        aggregates[v.codice].totaleApplicato += v.applicato || 0.0;
        aggregates[v.codice].totaleDifferenza += v.differenza || 0.0;
      });
    });
    return Object.values(aggregates).sort((a, b) => Math.abs(b.totaleDifferenza) - Math.abs(a.totaleDifferenza));
  };

  const fetchAtto = async () => {
    setLoadingAtto(true);
    try {
      const response = await axios.get(`${API_URL}/sindacato/vertenza/${id}/atto/${selectedAttoTipo}`);
      setAttoText(response.data.testo);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento della bozza dell\'atto.');
    } finally {
      setLoadingAtto(false);
    }
  };

  const handleSaveAnagrafica = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        titolo: pratica.titolo,
        lavoratoreNome: pratica.lavoratoreNome,
        lavoratoreCf: pratica.lavoratoreCf,
        datoreNome: pratica.datoreNome,
        datorePiva: pratica.datorePiva,
        ccnlNome: pratica.ccnlNome,
        ccnlCodice: pratica.ccnlCodice,
        livello: pratica.livello,
        livelloApplicato: pratica.livelloApplicato,
        dataAssunzione: pratica.dataAssunzione,
        dataCessazione: pratica.dataCessazione,
        periodoDa: (pratica.dataAssunzione || '').length >= 7 ? pratica.dataAssunzione.slice(0, 7) : pratica.periodoDa,
        periodoA: (pratica.dataCessazione || '').length >= 7 ? pratica.dataCessazione.slice(0, 7) : (pratica.periodoA || (pratica.dataAssunzione || '').slice(0, 7)),
        regioneResidenza: pratica.regioneResidenza,
        comuneResidenza: pratica.comuneResidenza,
        addizionaleRegionalePct: pratica.addizionaleRegionalePct,
        addizionaleComunalePct: pratica.addizionaleComunalePct,
        note: pratica.note,
        propostaTransazione: pratica.propostaTransazione
      });
      setPratica(response.data);
      toast.success('Dati anagrafici salvati e conteggi ricalcolati.');
    } catch (err) {
      console.error(err);
      toast.error('Errore durante il salvataggio.');
    }
  };

  const handleSaveScenari = async () => {
    try {
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        opzioni
      });
      setPratica(response.data);
      toast.success('Opzioni scenario applicate.');
    } catch (err) {
      console.error(err);
      toast.error('Errore durante l\'aggiornamento dello scenario.');
    }
  };

  // Monthly values edit
  const openMonthEditor = (month) => {
    setSelectedMonth(month.annoMese);
    setMonthEditData({
      vociRichieste: month.vociRichieste ? [...month.vociRichieste] : [],
      vociApplicate: month.vociApplicate ? [...month.vociApplicate] : []
    });
    setReplicaFinoAMese(pratica?.periodoA || '');
  };

  const handleReplicateMonthData = async () => {
    if (!replicaFinoAMese || !/^\d{4}-\d{2}$/.test(replicaFinoAMese)) {
      toast.error('Inserisci un mese di destinazione valido nel formato YYYY-MM.');
      return;
    }
    
    if (replicaFinoAMese <= selectedMonth) {
      toast.error('Il mese di destinazione deve essere successivo al mese corrente.');
      return;
    }

    const updatedPeriodi = pratica.periodi.map(p => {
      // Se il mese rientra nell'intervallo da selectedMonth (escluso) fino a replicaFinoAMese (incluso)
      if (p.annoMese > selectedMonth && p.annoMese <= replicaFinoAMese) {
        return {
          ...p,
          vociRichieste: monthEditData.vociRichieste.map(v => ({ ...v })),
          vociApplicate: monthEditData.vociApplicate.map(v => ({ ...v }))
        };
      }
      // Altrimenti, se è il mese correntemente editato
      if (p.annoMese === selectedMonth) {
        return {
          ...p,
          vociRichieste: monthEditData.vociRichieste,
          vociApplicate: monthEditData.vociApplicate
        };
      }
      return p;
    });

    try {
      toast.info('Replica e ricalcolo in corso...');
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        periodi: updatedPeriodi
      });
      setPratica(response.data);
      setSelectedMonth(null);
      toast.success(`Voci di ${selectedMonth} replicate fino a ${replicaFinoAMese} con successo!`);
    } catch (err) {
      console.error(err);
      toast.error('Errore durante la replica delle voci.');
    }
  };

  const addVoce = (lato) => {
    const defaultVoce = { codice: 'PB', descrizione: 'Voce Retributiva', importo: 0.0 };
    setMonthEditData(prev => ({
      ...prev,
      [lato]: [...prev[lato], defaultVoce]
    }));
  };

  const removeVoce = (lato, index) => {
    setMonthEditData(prev => ({
      ...prev,
      [lato]: prev[lato].filter((_, idx) => idx !== index)
    }));
  };

  const updateVoceField = (lato, index, field, value) => {
    setMonthEditData(prev => {
      const list = [...prev[lato]];
      list[index] = { ...list[index], [field]: field === 'importo' ? parseFloat(value) || 0.0 : value };
      return { ...prev, [lato]: list };
    });
  };

  const saveMonthData = async () => {
    const updatedPeriodi = pratica.periodi.map(p => {
      if (p.annoMese === selectedMonth) {
        return {
          ...p,
          vociRichieste: monthEditData.vociRichieste,
          vociApplicate: monthEditData.vociApplicate
        };
      }
      return p;
    });

    try {
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        periodi: updatedPeriodi
      });
      setPratica(response.data);
      setSelectedMonth(null);
      toast.success(`Dati per ${selectedMonth} salvati.`);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel salvataggio delle voci mensili.');
    }
  };

  // Add Importo Aggiuntivo
  const handleAddAggiuntivo = async (e) => {
    e.preventDefault();
    if (!newAggiuntivo.descrizione || newAggiuntivo.importo <= 0) {
      toast.error('Inserisci descrizione e importo valido.');
      return;
    }
    const item = { ...newAggiuntivo, id: str_uuid() };
    const list = [...(pratica.importiAggiuntivi || []), item];
    
    try {
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        importiAggiuntivi: list
      });
      setPratica(response.data);
      setNewAggiuntivo({ tipo: 'ferie', descrizione: '', importo: 0, applicaAccessori: true });
      toast.success('Voce aggiuntiva inserita con successo.');
    } catch (err) {
      console.error(err);
      toast.error('Errore nell\'aggiunta dell\'importo.');
    }
  };

  const handleDeleteAggiuntivo = async (aggiuntivoId) => {
    const list = pratica.importiAggiuntivi.filter(item => item.id !== aggiuntivoId);
    try {
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        importiAggiuntivi: list
      });
      setPratica(response.data);
      toast.success('Voce aggiuntiva rimossa.');
    } catch (err) {
      console.error(err);
      toast.error('Errore nella rimozione.');
    }
  };

  const handleBulkGenerateScatti = async () => {
    if (!genScatti.mesePrimoScatto || !/^\d{4}-\d{2}$/.test(genScatti.mesePrimoScatto)) {
      toast.error('Inserisci un mese di inizio valido nel formato YYYY-MM.');
      return;
    }
    if (genScatti.importoScatto <= 0) {
      toast.error('Inserisci un importo scatto valido.');
      return;
    }
    
    const getMonthsDiff = (start, end) => {
      const [sY, sM] = start.split('-').map(Number);
      const [eY, eM] = end.split('-').map(Number);
      return (eY - sY) * 12 + (eM - sM);
    };

    const updatedPeriodi = pratica.periodi.map(p => {
      let numScatti = genScatti.scattiPartenza;
      
      const diff = getMonthsDiff(genScatti.mesePrimoScatto, p.annoMese);
      if (diff >= 0) {
        numScatti = genScatti.scattiPartenza + 1 + Math.floor(diff / genScatti.frequenzaMesi);
        if (numScatti > genScatti.maxScatti) {
          numScatti = genScatti.maxScatti;
        }
      }
      
      // Filtra via scatti preesistenti con lo stesso codice
      const vociRichieste = (p.vociRichieste || [])
        .filter(v => v.codice !== genScatti.codiceVoce);
        
      if (numScatti > 0) {
        const importoTotal = Math.round(numScatti * genScatti.importoScatto * 100) / 100;
        vociRichieste.push({
          codice: genScatti.codiceVoce,
          descrizione: `${genScatti.descrizioneVoce} (${numScatti})`,
          importo: importoTotal,
          imponibileContributi: true,
          imponibileFiscale: true
        });
      }
      
      return {
        ...p,
        vociRichieste
      };
    });

    try {
      toast.info('Generazione e ricalcolo in corso...');
      const response = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
        periodi: updatedPeriodi
      });
      setPratica(response.data);
      toast.success('Progressione scatti generata e applicata con successo!');
    } catch (err) {
      console.error(err);
      toast.error('Errore durante l\'applicazione in blocco degli scatti.');
    }
  };

  const handleResetVertenza = async () => {
    if (!window.confirm("Vuoi davvero ripristinare il conteggio iniziale? Questo cancellerà TUTTE le modifiche manuali, gli scatti generati e gli importi aggiuntivi inseriti per questa pratica.")) {
      return;
    }
    try {
      toast.info('Ripristino in corso...');
      const response = await axios.post(`${API_URL}/sindacato/vertenza/${id}/reset`);
      setPratica(response.data);
      setSelectedMonth(null);
      toast.success('Pratica ripristinata con successo ai valori di default.');
    } catch (err) {
      console.error(err);
      toast.error('Errore durante il ripristino del conteggio.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(attoText);
    toast.success('Testo copiato negli appunti!');
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const str_uuid = () => {
    return 'item-' + Math.random().toString(36).substr(2, 9);
  };

  const handleDownloadExcel = async () => {
    try {
      toast.info("Generazione report Excel in corso...");
      const response = await axios.get(`${API_URL}/sindacato/vertenza/${id}/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vertenza_${pratica.numero || id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel scaricato con successo!");
    } catch (err) {
      console.error(err);
      toast.error("Errore nel download del file Excel.");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info("Generazione Relazione PDF in corso...");
      const response = await axios.get(`${API_URL}/sindacato/vertenza/${id}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relazione_vertenza_${pratica.numero || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato con successo!");
    } catch (err) {
      console.error(err);
      toast.error("Errore nel download del file PDF.");
    }
  };

  const handleDownloadAttoPDF = async () => {
    try {
      toast.info(`Generazione bozza ${selectedAttoTipo} in corso...`);
      const response = await axios.get(`${API_URL}/sindacato/vertenza/${id}/atto/${selectedAttoTipo}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedAttoTipo}_${pratica.numero || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bozza atto scaricata con successo!");
    } catch (err) {
      console.error(err);
      toast.error("Errore nel download della bozza PDF.");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="spinner animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !pratica) {
    return (
      <DashboardLayout>
        <div className="p-12 text-center max-w-md mx-auto space-y-4 bg-white border border-slate-200 rounded-lg shadow-sm mt-10">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
          <h3 className="text-lg font-bold text-slate-800">Pratica Non Trovata</h3>
          <p className="text-sm text-slate-500">{error || 'La pratica di vertenza richiesta non esiste o non può essere caricata.'}</p>
          <Button onClick={() => navigate('/vertenze')} className="bg-forest text-white">
            Torna alle Vertenze
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const r = pratica.risultato || {};
  
  return (
    <DashboardLayout>
      <div className="space-y-6 p-1">
        {/* Back and Title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/vertenze')}
              className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 transition"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-bold text-forest bg-forest/5 px-2 py-0.5 border border-forest/15 rounded-md">
                  {pratica.numero}
                </span>
                <span className="text-xs uppercase font-bold tracking-wide text-slate-500">• {pratica.stato}</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mt-1">{pratica.titolo}</h1>
            </div>
          </div>
          
          {/* Main Action Buttons */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={handleDownloadExcel}
              className="border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition flex items-center gap-1.5"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              Esporta Excel
            </Button>
            <Button 
              onClick={handleDownloadPDF}
              className="bg-forest hover:bg-forest/90 text-white font-medium shadow-sm transition flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" />
              Scarica PDF
            </Button>
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-slate-200 overflow-x-auto gap-6 text-sm font-semibold scrollbar-none">
          {[
            { id: 'anagrafica', label: 'Anagrafica e CCNL', icon: Edit3 },
            { id: 'periodi', label: 'Editor Periodi', icon: Calendar },
            { id: 'quadro', label: 'Quadro Riepilogativo', icon: Scale },
            { id: 'scenari', label: 'Simulatore Scenari', icon: TrendingUp },
            { id: 'atti', label: 'Atti Legali', icon: FileText }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 pb-3 border-b-2 transition font-medium ${
                  isActive 
                    ? 'border-forest text-forest font-bold' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        {activeTab === 'anagrafica' && (
          <form onSubmit={handleSaveAnagrafica} className="space-y-6 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <h3 className="text-md font-bold text-slate-800 border-b pb-2">Informazioni Anagrafiche</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Titolo Pratica *</label>
                <input
                  type="text"
                  required
                  value={pratica.titolo}
                  onChange={(e) => setPratica({ ...pratica, titolo: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:ring-1 focus:ring-forest"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Numero Pratica (Audit)</label>
                <input
                  type="text"
                  disabled
                  value={pratica.numero}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm bg-slate-50 font-mono text-slate-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Lavoratore *</label>
                <input
                  type="text"
                  required
                  value={pratica.lavoratoreNome}
                  onChange={(e) => setPratica({ ...pratica, lavoratoreNome: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Codice Fiscale Lavoratore</label>
                <input
                  type="text"
                  value={pratica.lavoratoreCf || ''}
                  onChange={(e) => setPratica({ ...pratica, lavoratoreCf: e.target.value.toUpperCase() })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Datore di Lavoro *</label>
                <input
                  type="text"
                  required
                  value={pratica.datoreNome}
                  onChange={(e) => setPratica({ ...pratica, datoreNome: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">P. IVA / C.F. Datore</label>
                <input
                  type="text"
                  value={pratica.datorePiva || ''}
                  onChange={(e) => setPratica({ ...pratica, datorePiva: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
                />
              </div>
            </div>

            <h3 className="text-md font-bold text-slate-800 border-b pb-2 pt-4">Parametri CCNL ed Economici</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">CCNL Applicato</label>
                <select
                  value={pratica.ccnlCodice || ''}
                  onChange={(e) => {
                    const selected = ccnlList.find(c => c.id === e.target.value);
                    if (selected) {
                      setPratica({
                        ...pratica,
                        ccnlCodice: selected.id,
                        ccnlNome: selected.nome
                      });
                    }
                  }}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white"
                >
                  <option value="">Seleziona CCNL...</option>
                  {ccnlList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                  {pratica.ccnlCodice && !ccnlList.some(c => c.id === pratica.ccnlCodice) && (
                    <option value={pratica.ccnlCodice}>{pratica.ccnlNome}</option>
                  )}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Liv. Spettante</label>
                  <input
                    type="text"
                    value={pratica.livello || ''}
                    onChange={(e) => setPratica({ ...pratica, livello: e.target.value })}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Liv. Inquadrato</label>
                  <input
                    type="text"
                    value={pratica.livelloApplicato || ''}
                    onChange={(e) => setPratica({ ...pratica, livelloApplicato: e.target.value })}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Data Assunzione</label>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={pratica.dataAssunzione || ''}
                    onChange={(e) => setPratica({ ...pratica, dataAssunzione: e.target.value })}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Data Cessazione</label>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={pratica.dataCessazione || ''}
                    onChange={(e) => setPratica({ ...pratica, dataCessazione: e.target.value })}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Regione Residenza</label>
                  <input
                    type="text"
                    value={pratica.regioneResidenza || ''}
                    onChange={(e) => setPratica({ ...pratica, regioneResidenza: e.target.value })}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Addiz. Reg. (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={pratica.addizionaleRegionalePct || 0}
                    onChange={(e) => setPratica({ ...pratica, addizionaleRegionalePct: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Note Pratica</label>
              <textarea
                rows="3"
                value={pratica.note || ''}
                onChange={(e) => setPratica({ ...pratica, note: e.target.value })}
                className="w-full border border-slate-200 rounded-md p-2 text-sm"
              />
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button type="submit" className="bg-forest hover:bg-forest/90 text-white font-medium flex items-center gap-1.5">
                <Save className="w-4 h-4" />
                Salva Modifiche e Ricalcola
              </Button>
            </div>
          </form>
        )}

        {activeTab === 'periodi' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Months List */}
            <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden flex flex-col h-[550px]">
              <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-sm text-slate-700">
                Seleziona Mese da Rettificare
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100 scrollbar-thin">
                {pratica.periodi && pratica.periodi.map(month => {
                  const isSelected = selectedMonth === month.annoMese;
                  const calcPeriod = (pratica.risultato?.periodi || []).find(p => p.annoMese === month.annoMese);
                  const diff = calcPeriod ? (calcPeriod.differenzaLorda || 0) : 0;
                  return (
                    <div 
                      key={month.annoMese}
                      onClick={() => openMonthEditor(month)}
                      className={`p-3.5 flex justify-between items-center cursor-pointer transition ${
                        isSelected ? 'bg-forest/10 border-l-4 border-forest' : 'hover:bg-slate-50/70'
                      }`}
                    >
                      <div className="font-mono text-sm font-bold text-slate-800">{month.annoMese}</div>
                      <div className={`text-sm font-bold tabular-nums ${diff > 0 ? 'text-rose-600' : 'text-slate-600'}`}>
                        {formatCurrency(diff)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Month Editor Panel */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-6 flex flex-col justify-between min-h-[550px]">
              {!selectedMonth ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full items-start">
                  <div className="flex flex-col items-center justify-center text-center p-6 text-slate-400 border-b md:border-b-0 md:border-r border-slate-100 h-full min-h-[250px] justify-center my-auto">
                    <Edit3 className="w-12 h-12 mb-3 text-slate-300 mx-auto" />
                    <p className="text-sm font-medium leading-relaxed max-w-[240px] mb-4">
                      Seleziona un mese dalla lista a sinistra per rettificare le singole voci retributive erogate o spettanti.
                    </p>
                    <div className="w-full max-w-[240px]">
                      <Button
                        onClick={handleResetVertenza}
                        variant="outline"
                        className="w-full border-rose-250 text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-semibold text-xs py-2 flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Ripristina Conteggio Iniziale
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-2 space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-forest" />
                        Generatore Automatico Scatti
                      </h4>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-normal">
                        Popola gli scatti di anzianità garantiti su tutti i mesi della pratica.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Mese Primo Scatto</label>
                        <input
                          type="text"
                          placeholder="YYYY-MM"
                          value={genScatti.mesePrimoScatto}
                          onChange={(e) => setGenScatti({ ...genScatti, mesePrimoScatto: e.target.value })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs font-mono bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Importo Singolo (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={genScatti.importoScatto}
                          onChange={(e) => setGenScatti({ ...genScatti, importoScatto: parseFloat(e.target.value) || 0 })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs font-mono bg-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Freq. (Mesi)</label>
                        <input
                          type="number"
                          value={genScatti.frequenzaMesi}
                          onChange={(e) => setGenScatti({ ...genScatti, frequenzaMesi: parseInt(e.target.value) || 36 })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs font-mono bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Scatti Partenza</label>
                        <input
                          type="number"
                          value={genScatti.scattiPartenza}
                          onChange={(e) => setGenScatti({ ...genScatti, scattiPartenza: parseInt(e.target.value) || 0 })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs font-mono bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Max Scatti</label>
                        <input
                          type="number"
                          value={genScatti.maxScatti}
                          onChange={(e) => setGenScatti({ ...genScatti, maxScatti: parseInt(e.target.value) || 10 })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs font-mono bg-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-1 space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Codice Voce</label>
                        <input
                          type="text"
                          value={genScatti.codiceVoce}
                          onChange={(e) => setGenScatti({ ...genScatti, codiceVoce: e.target.value.toUpperCase() })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs font-mono bg-white"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Descrizione</label>
                        <input
                          type="text"
                          value={genScatti.descrizioneVoce}
                          onChange={(e) => setGenScatti({ ...genScatti, descrizioneVoce: e.target.value })}
                          className="w-full border border-slate-200 rounded p-1.5 text-xs bg-white"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleBulkGenerateScatti}
                      className="w-full bg-forest text-white hover:bg-forest/90 font-medium text-xs py-2 mt-2 flex items-center justify-center gap-1.5 shadow-sm"
                    >
                      <TrendingUp className="w-3.5 h-3.5" />
                      Applica Scatti a Tutti i Mesi
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6 flex-1 flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-2">
                      <h3 className="font-mono text-md font-bold text-slate-800">Rettifica Voci: {selectedMonth}</h3>
                      <span className="text-xs text-muted-foreground">Competenze in Lordo (€)</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      
                      {/* Voci Richieste (Spettante) */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-forest">Voci Spettanti (Richieste)</h4>
                          <Button size="sm" variant="ghost" className="text-forest hover:bg-forest/5 p-1 text-xs" onClick={() => addVoce('vociRichieste')}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
                          </Button>
                        </div>
                        <div className="space-y-2 border border-slate-100 p-3 rounded-md bg-slate-50/50 min-h-[220px] max-h-[300px] overflow-y-auto">
                          {monthEditData.vociRichieste.length === 0 && (
                            <p className="text-xs text-slate-400 text-center py-6">Nessuna voce spettante inserita.</p>
                          )}
                          {monthEditData.vociRichieste.map((v, idx) => (
                            <div key={idx} className="flex gap-1.5 items-center">
                              <input 
                                type="text" 
                                placeholder="Codice" 
                                value={v.codice} 
                                onChange={(e) => updateVoceField('vociRichieste', idx, 'codice', e.target.value)} 
                                className="w-16 border border-slate-200 rounded p-1 text-xs font-mono"
                              />
                              <input 
                                type="text" 
                                placeholder="Descrizione" 
                                value={v.descrizione} 
                                onChange={(e) => updateVoceField('vociRichieste', idx, 'descrizione', e.target.value)} 
                                className="flex-1 border border-slate-200 rounded p-1 text-xs"
                              />
                              <input 
                                type="number" 
                                step="0.01" 
                                placeholder="Importo" 
                                value={v.importo || ''} 
                                onChange={(e) => updateVoceField('vociRichieste', idx, 'importo', e.target.value)} 
                                className="w-20 border border-slate-200 rounded p-1 text-xs font-mono text-right"
                              />
                              <button onClick={() => removeVoce('vociRichieste', idx)} className="text-slate-400 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Voci Applicate (Erogate) */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-rose-600">Voci Erogate (Applicate)</h4>
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 p-1 text-xs" onClick={() => addVoce('vociApplicate')}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
                          </Button>
                        </div>
                        <div className="space-y-2 border border-slate-100 p-3 rounded-md bg-slate-50/50 min-h-[220px] max-h-[300px] overflow-y-auto">
                          {monthEditData.vociApplicate.length === 0 && (
                            <p className="text-xs text-slate-400 text-center py-6">Nessuna voce erogata inserita.</p>
                          )}
                          {monthEditData.vociApplicate.map((v, idx) => (
                            <div key={idx} className="flex gap-1.5 items-center">
                              <input 
                                type="text" 
                                placeholder="Codice" 
                                value={v.codice} 
                                onChange={(e) => updateVoceField('vociApplicate', idx, 'codice', e.target.value)} 
                                className="w-16 border border-slate-200 rounded p-1 text-xs font-mono"
                              />
                              <input 
                                type="text" 
                                placeholder="Descrizione" 
                                value={v.descrizione} 
                                onChange={(e) => updateVoceField('vociApplicate', idx, 'descrizione', e.target.value)} 
                                className="flex-1 border border-slate-200 rounded p-1 text-xs"
                              />
                              <input 
                                type="number" 
                                step="0.01" 
                                placeholder="Importo" 
                                value={v.importo || ''} 
                                onChange={(e) => updateVoceField('vociApplicate', idx, 'importo', e.target.value)} 
                                className="w-20 border border-slate-200 rounded p-1 text-xs font-mono text-right"
                              />
                              <button onClick={() => removeVoce('vociApplicate', idx)} className="text-slate-400 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  </div>

                  <div className="border-t pt-4 mt-2 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div className="space-y-0.5">
                        <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
                          <Copy className="w-3.5 h-3.5 text-forest" />
                          Replica Voci in Blocco
                        </h4>
                        <p className="text-[10px] text-slate-500 leading-normal max-w-[320px]">
                          Copia le voci spettanti ed erogate di questo mese su un intervallo di mesi successivi.
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 font-medium">
                          <span>Fino al mese:</span>
                          <input 
                            type="text" 
                            placeholder="YYYY-MM"
                            value={replicaFinoAMese}
                            onChange={(e) => setReplicaFinoAMese(e.target.value)}
                            className="w-24 border border-slate-250 rounded p-1.5 text-xs font-mono bg-white text-center focus:ring-1 focus:ring-forest"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={handleReplicateMonthData}
                          className="border-forest text-forest hover:bg-forest/5 text-xs font-semibold px-4"
                        >
                          Replica Voci
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <Button variant="outline" onClick={() => setSelectedMonth(null)}>
                        Annulla
                      </Button>
                      <Button onClick={saveMonthData} className="bg-forest hover:bg-forest/90 text-white font-medium shadow-sm">
                        Salva Modifiche Mensili
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {activeTab === 'quadro' && (
          <div className="space-y-6">
            
            {/* Visual Credit Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border border-slate-200 shadow-xs">
                <CardHeader className="pb-1">
                  <CardTitle className="text-xs font-bold text-slate-500 uppercase">Differenze Retributive</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-black text-slate-800">{formatCurrency(r.differenzaLorda || 0)}</div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Lordo Totale Spettante vs Inquadrato</p>
                </CardContent>
              </Card>
              
              <Card className="border border-slate-200 shadow-xs">
                <CardHeader className="pb-1">
                  <CardTitle className="text-xs font-bold text-slate-500 uppercase">Rivalutazione (Art 429)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-black text-slate-800">{formatCurrency(r.rivalutazione || 0)}</div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Indicizzazione FOI ISTAT storica</p>
                </CardContent>
              </Card>
              
              <Card className="border border-slate-200 shadow-xs">
                <CardHeader className="pb-1">
                  <CardTitle className="text-xs font-bold text-slate-500 uppercase">Interessi Legali</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-black text-slate-800">{formatCurrency(r.interessi || 0)}</div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Su capitale progressivamente rivalutato</p>
                </CardContent>
              </Card>
              
              <Card className="border border-slate-200 shadow-xs">
                <CardHeader className="pb-1">
                  <CardTitle className="text-xs font-bold text-slate-500 uppercase">Quota TFR (Art 2120)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-black text-slate-800">{formatCurrency(r.tfrDifferenze || 0)}</div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Accantonato + rivalutazioni 31/12</p>
                </CardContent>
              </Card>
            </div>

            {/* Total Balance Hero Card */}
            <div className="bg-gradient-to-tr from-forest to-emerald-950 text-white p-6 rounded-lg shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="text-lg font-bold">Credito Complessivo Richiesto</h3>
                <p className="text-emerald-250 text-xs mt-1">Calcolato fino alla data di riferimento: {r.calcolatoAl}</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black tracking-tight">{formatCurrency(r.totaleCredito || 0)}</div>
                <div className="text-[10px] text-emerald-200/80 mt-0.5">Somma Differenze + Rivalutazione + Interessi + TFR + Importi Aggiuntivi</div>
              </div>
            </div>

            {/* Importi Aggiuntivi Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Insert Form */}
              <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <h4 className="text-sm font-bold text-slate-800 border-b pb-2 mb-4">Aggiungi Importo a Credito (Ferie, preavviso...)</h4>
                <form onSubmit={handleAddAggiuntivo} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Tipo Indennità</label>
                    <select
                      value={newAggiuntivo.tipo}
                      onChange={(e) => setNewAggiuntivo({ ...newAggiuntivo, tipo: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white"
                    >
                      <option value="ferie">Ferie Non Godute</option>
                      <option value="permessi">Permessi Non Goduti</option>
                      <option value="preavviso">Indennità di Preavviso</option>
                      <option value="licenziamento">Indennità di Licenziamento</option>
                      <option value="straordinario">Straordinari Fuori Busta</option>
                      <option value="altro">Altro Credito</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Descrizione</label>
                    <input
                      type="text"
                      required
                      placeholder="es. Liquidazione 10 gg ferie residue"
                      value={newAggiuntivo.descrizione}
                      onChange={(e) => setNewAggiuntivo({ ...newAggiuntivo, descrizione: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-600">Importo Lordo (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={newAggiuntivo.importo || ''}
                        onChange={(e) => setNewAggiuntivo({ ...newAggiuntivo, importo: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
                      />
                    </div>
                    <div className="flex items-center pt-5 justify-center">
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-600">
                        <input
                          type="checkbox"
                          checked={newAggiuntivo.applicaAccessori}
                          onChange={(e) => setNewAggiuntivo({ ...newAggiuntivo, applicaAccessori: e.target.checked })}
                          className="rounded border-slate-350 focus:ring-forest text-forest"
                        />
                        Applica rivalutazione/int.
                      </label>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-forest text-white hover:bg-forest/90 font-medium">
                    Aggiungi Credito
                  </Button>
                </form>
              </div>

              {/* Items List */}
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <h4 className="text-sm font-bold text-slate-800 border-b pb-2 mb-4">Crediti e Indennità Aggiuntive Inserite</h4>
                {(!pratica.importiAggiuntivi || pratica.importiAggiuntivi.length === 0) ? (
                  <div className="text-center py-12 text-slate-400">
                    <FolderOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-xs font-medium">Nessuna voce aggiuntiva presente. Usa il form a sinistra per aggiungerne.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {pratica.importiAggiuntivi.map((item) => (
                      <div key={item.id} className="py-3.5 flex justify-between items-center">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md mr-2">
                            {item.tipo}
                          </span>
                          <span className="text-sm font-semibold text-slate-800">{item.descrizione}</span>
                          {item.applicaAccessori && (
                            <span className="text-[10px] text-forest ml-2" title="Soggetto a rivalutazione e interessi">
                              (+ art. 429)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm font-bold text-slate-900">{formatCurrency(item.importo)}</span>
                          <button 
                            onClick={() => handleDeleteAggiuntivo(item.id)}
                            className="text-slate-400 hover:text-red-600 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Riepilogo Voci Table */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 mt-6">
              <div className="border-b pb-2 mb-4">
                <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-forest" />
                  Riepilogo Dettagliato Differenze per Voce Retributiva
                </h4>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-normal">
                  Questo prospetto aggrega le differenze spettanti ed erogate per singola voce retributiva sull'intero periodo del conteggio, rendendo chiara la composizione delle pretese.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="p-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-24">Codice</th>
                      <th className="p-3 text-xs font-bold uppercase tracking-wider text-slate-500">Descrizione Voce</th>
                      <th className="p-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Totale Spettante (Richiesto)</th>
                      <th className="p-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Totale Erogato (Applicato)</th>
                      <th className="p-3 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Totale Differenza Lorda</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {getAggregatedVociDiff().map((v) => (
                      <tr key={v.codice} className="hover:bg-slate-50/50">
                        <td className="p-3 font-mono text-xs font-bold text-slate-700">{v.codice}</td>
                        <td className="p-3 text-slate-800 font-medium">{v.descrizione}</td>
                        <td className="p-3 text-right font-mono tabular-nums text-slate-700">{formatCurrency(v.totaleRichiesto)}</td>
                        <td className="p-3 text-right font-mono tabular-nums text-slate-700">{formatCurrency(v.totaleApplicato)}</td>
                        <td className={`p-3 text-right font-mono tabular-nums font-bold ${v.totaleDifferenza > 0 ? 'text-rose-600' : 'text-slate-600'}`}>
                          {formatCurrency(v.totaleDifferenza)}
                        </td>
                      </tr>
                    ))}
                    {getAggregatedVociDiff().length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-slate-400">
                          Nessun dato di dettaglio disponibile.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'scenari' && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-6">
            <h3 className="text-md font-bold text-slate-800 border-b pb-2 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-forest" />
              Simulazione Scenari conciliativi / Transazioni
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Parameters Column */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Impostazioni Calcolo Scenario</h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-slate-50 rounded border border-slate-100">
                      <input 
                        type="checkbox"
                        checked={opzioni.escludiTfr}
                        onChange={(e) => setOpzioni({ ...opzioni, escludiTfr: e.target.checked })}
                        className="rounded border-slate-350 focus:ring-forest text-forest"
                      />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Escludi differenze TFR (art. 2120 c.c.)</div>
                        <div className="text-xs text-muted-foreground">Non include l'accantonamento TFR del 6.91% sulle differenze retributive</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-slate-50 rounded border border-slate-100">
                      <input 
                        type="checkbox"
                        checked={opzioni.escludiAccessori}
                        onChange={(e) => setOpzioni({ ...opzioni, escludiAccessori: e.target.checked })}
                        className="rounded border-slate-350 focus:ring-forest text-forest"
                      />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Escludi accessori ex art. 429 c.p.c.</div>
                        <div className="text-xs text-muted-foreground">Rimuove completamente rivalutazione ISTAT e interessi legali</div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Parzializzazione Accessori</h4>
                  
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-semibold text-slate-600">
                      <span>Rivalutazione ISTAT (% da riconoscere):</span>
                      <span className="font-mono">{opzioni.percentualeRivalutazione}%</span>
                    </div>
                    <input 
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={opzioni.percentualeRivalutazione}
                      onChange={(e) => setOpzioni({ ...opzioni, percentualeRivalutazione: parseFloat(e.target.value) })}
                      className="w-full accent-forest"
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-semibold text-slate-600">
                      <span>Interessi Legali (% da riconoscere):</span>
                      <span className="font-mono">{opzioni.percentualeInteressi}%</span>
                    </div>
                    <input 
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={opzioni.percentualeInteressi}
                      onChange={(e) => setOpzioni({ ...opzioni, percentualeInteressi: parseFloat(e.target.value) })}
                      className="w-full accent-forest"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <Button onClick={handleSaveScenari} className="w-full bg-forest text-white hover:bg-forest/90 font-medium">
                    Applica Scenario e Ricalcola
                  </Button>
                </div>
              </div>

              {/* Comparison Result Column */}
              <div className="space-y-6 bg-slate-50 p-6 rounded-lg border border-slate-100 flex flex-col justify-between">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Risultato Scenario Attivo</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b pb-1.5">
                      <span className="text-slate-600">Capitale Differenze (Lordo):</span>
                      <span className="font-mono font-semibold text-slate-800">{formatCurrency(r.differenzaLorda || 0)}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1.5">
                      <span className="text-slate-600">Rivalutazione Monetaria:</span>
                      <span className="font-mono font-semibold text-slate-800">{formatCurrency(r.rivalutazione || 0)}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1.5">
                      <span className="text-slate-600">Interessi Legali:</span>
                      <span className="font-mono font-semibold text-slate-800">{formatCurrency(r.interessi || 0)}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1.5">
                      <span className="text-slate-600">TFR su Differenze:</span>
                      <span className="font-mono font-semibold text-slate-800">{formatCurrency(r.tfrDifferenze || 0)}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1.5">
                      <span className="text-slate-600">Importi Aggiuntivi:</span>
                      <span className="font-mono font-semibold text-slate-800">{formatCurrency(r.totaleAggiuntivi || 0)}</span>
                    </div>
                  </div>

                  {/* Pro Comparison Tool */}
                  <div className="mt-6 border-t pt-4 space-y-4">
                    <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Analisi Comparativa Costo / Netto</h5>
                    
                    <div className="p-3 bg-emerald-50 border border-emerald-250 rounded-md">
                      <div className="text-xs font-bold text-emerald-800">Netto Estimato in Tasca al Lavoratore</div>
                      <div className="text-xl font-black text-emerald-700 mt-1">
                        {formatCurrency(
                          (r.differenzaNetta || 0) + 
                          (r.rivalutazione || 0) + 
                          (r.interessi || 0) + 
                          (r.tfrDifferenze || 0) + 
                          (r.totaleAggiuntivi || 0)
                        )}
                      </div>
                      <div className="text-[10px] text-emerald-600/90 mt-0.5">Differenze nette + rivalutazione + interessi + TFR</div>
                    </div>

                    <div className="p-3 bg-rose-50 border border-rose-250 rounded-md">
                      <div className="text-xs font-bold text-rose-800">Costo Effettivo Stimato per l'Azienda</div>
                      <div className="text-xl font-black text-rose-700 mt-1">
                        {formatCurrency(
                          ((r.differenzaLorda || 0) * 1.30) + 
                          (r.rivalutazione || 0) + 
                          (r.interessi || 0) + 
                          (r.tfrDifferenze || 0) + 
                          (r.totaleAggiuntivi || 0)
                        )}
                      </div>
                      <div className="text-[10px] text-rose-600/90 mt-0.5">Lordo differenze + INPS azienda (~30%) + accessori</div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 text-right">
                  <div className="text-xs font-bold text-slate-500 uppercase">Totale Credito Richiesto (Lordo)</div>
                  <div className="text-3xl font-black text-forest mt-1">{formatCurrency(r.totaleCredito || 0)}</div>
                </div>
              </div>

            </div>

            {/* Cruscotto Transattivo / Saldo e Stralcio */}
            <div className="mt-8 border-t border-dashed border-slate-200 pt-6 space-y-4">
              <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Scale className="w-4 h-4 text-forest" />
                Simulazione Accordo Transattivo (Saldo e Stralcio)
              </h4>
              <p className="text-xs text-muted-foreground">
                Imposta una cifra di accordo per simulare in tempo reale il risparmio totale ditta (cuneo fiscale) ed il netto stimato spettante al lavoratore in sede protetta.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3 bg-slate-50/50 p-4 border border-slate-200 rounded-md">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block">Proposta Transattiva (Lordo €)</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="100"
                      value={offertaTransazione || ''}
                      onChange={(e) => setOffertaTransazione(parseFloat(e.target.value) || 0)}
                      className="border border-slate-250 rounded-md p-2 text-sm font-mono w-full focus:ring-1 focus:ring-forest bg-white"
                      placeholder="Inserisci importo, es. 5000"
                    />
                    <Button 
                      onClick={async () => {
                        try {
                          const res = await axios.put(`${API_URL}/sindacato/vertenza/${id}`, {
                            propostaTransazione: offertaTransazione
                          });
                          setPratica(res.data);
                          toast.success('Accordo transattivo aggiornato e salvato.');
                        } catch(e) {
                          toast.error('Errore nel salvataggio dell\'offerta.');
                        }
                      }}
                      className="bg-forest hover:bg-forest/90 text-white font-medium text-xs px-4"
                    >
                      Salva Offerta
                    </Button>
                  </div>
                  {r.totaleCredito > 0 && (
                    <div className="text-[11px] text-muted-foreground flex justify-between">
                      <span>Percentuale di Stralcio:</span>
                      <strong className="text-slate-800 font-mono">
                        {((offertaTransazione / r.totaleCredito) * 100).toFixed(1)}%
                      </strong>
                    </div>
                  )}
                </div>

                {offertaTransazione > 0 ? (
                  <div className="bg-emerald-50/60 p-4 border border-emerald-200 rounded-md space-y-3">
                    <h5 className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Impatto Finanziario Conciliazione</h5>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-slate-500 block">Risparmio Azienda vs Causa:</span>
                        <div className="font-mono font-black text-emerald-700 text-base mt-0.5">
                          {formatCurrency(
                            Math.max(
                              0, 
                              (((r.differenzaLorda || 0) * 1.30) + (r.rivalutazione || 0) + (r.interessi || 0) + (r.tfrDifferenze || 0) + (r.totaleAggiuntivi || 0)) - 
                              (offertaTransazione * 1.30)
                            )
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Netto Stimato Lavoratore:</span>
                        <div className="font-mono font-black text-slate-800 text-base mt-0.5">
                          {formatCurrency(offertaTransazione * 0.77)}
                          <span className="text-[9px] font-normal text-slate-500 ml-1">(est. tassaz.)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center border border-dashed border-slate-200 rounded-md p-4 bg-slate-50/20 text-slate-400 text-xs">
                    Inserisci una proposta lorda a sinistra per calcolare il cuneo transattivo.
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {activeTab === 'atti' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Act Selection */}
            <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
              <h4 className="text-sm font-bold text-slate-800 border-b pb-2 mb-2">Scegli Atto Giudiziale/Stragiudiziale</h4>
              <div className="flex flex-col gap-2.5">
                {[
                  { id: 'diffida', label: 'Diffida e Messa in Mora', desc: 'Sollecito formale ad adempiere entro 15 giorni con tabella accessori.' },
                  { id: 'conciliazione', label: 'Verbale di Conciliazione', desc: 'Accordo stragiudiziale transattivo con importo forfettario.' },
                  { id: 'ricorso', label: 'Ricorso ex art. 409 c.p.c.', desc: 'Bozza di ricorso al Giudice del Lavoro con quantificazione crediti.' },
                  { id: 'memoria', label: 'Memoria Difensiva', desc: 'Note autorizzate ed esposizione dei conteggi per udienza.' }
                ].map(atto => (
                  <button
                    key={atto.id}
                    onClick={() => setSelectedAttoTipo(atto.id)}
                    className={`p-3 text-left border rounded-lg transition ${
                      selectedAttoTipo === atto.id 
                        ? 'border-forest bg-forest/5 shadow-xs' 
                        : 'border-slate-200 hover:bg-slate-50/70'
                    }`}
                  >
                    <div className={`text-sm font-bold ${selectedAttoTipo === atto.id ? 'text-forest' : 'text-slate-800'}`}>
                      {atto.label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 leading-normal">{atto.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Draft Act Preview */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-6 flex flex-col justify-between h-[550px]">
              <div className="flex items-center justify-between border-b pb-2 mb-3">
                <h3 className="text-sm font-bold text-slate-800">Anteprima Bozza Documento</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-xs flex items-center gap-1" onClick={copyToClipboard}>
                    <Copy className="w-3.5 h-3.5" />
                    Copia
                  </Button>
                  <Button size="sm" onClick={handleDownloadAttoPDF} className="bg-forest text-white hover:bg-forest/90 text-xs flex items-center gap-1">
                    <Download className="w-3.5 h-3.5" />
                    PDF
                  </Button>
                </div>
              </div>

              {loadingAtto ? (
                <div className="flex items-center justify-center flex-1">
                  <div className="spinner animate-spin" />
                </div>
              ) : (
                <textarea
                  readOnly
                  value={attoText}
                  className="flex-1 w-full border border-slate-200 rounded-md p-4 text-xs font-mono bg-slate-50 focus:outline-none scrollbar-thin resize-none text-slate-700"
                />
              )}
            </div>

          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
